import { AxiosInstance, AxiosResponse, AxiosError, HttpStatusCode } from 'axios';
import IClientHTTP from '40.quickConnect.DataAccess/ClientHTTP/interface';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import RootStore from '30.quickConnect.Stores/RootStore';
import { errorHandler } from '80.quickConnect.Core/helpers';
import { i18n } from '80.quickConnect.Core/utils';
import { QcRequestConfig } from '40.quickConnect.DataAccess/axios/types';

class AxiosClientHTTP implements IClientHTTP {
  // Tag
  private static readonly TAG = '40.quickConnect.DataAccess/ClientHTTP/axios/index.ts';

  client: AxiosInstance;

  private static logger: CustomLogger = CustomLogger.getInstance();

  static rootStore: RootStore;

  private static instance: AxiosClientHTTP | null = null;

  private constructor(client: AxiosInstance, rootStore: RootStore) {
    this.client = client;
    AxiosClientHTTP.rootStore = rootStore;
  }

  static getInstance = (client?: AxiosInstance, rootStore?: RootStore): AxiosClientHTTP | never => {
    try {
      if (AxiosClientHTTP.instance === null && (client === undefined || rootStore === undefined))
        throw new Error(`A client and a rootStore must be provided when the instance is equals to null`);

      if (AxiosClientHTTP.instance === null) {
        this.instance = new AxiosClientHTTP(client!, rootStore!);
      }

      return AxiosClientHTTP.instance as AxiosClientHTTP;
    } catch (error: unknown) {
      errorHandler(AxiosClientHTTP.TAG, error, 'getInstance');
      throw error;
    }
  };

  login = async <T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: any): Promise<any> => {
    try {
      const response: R = await this.client.post<T, R>(url, data, config);

      AxiosClientHTTP.logger.debug(
        AxiosClientHTTP.TAG,
        `AxiosClientHTTP Login Function success - Url: ${AxiosClientHTTP.getUrlWithParams(url, config)} - Status: ${
          (response as unknown as AxiosResponse<T>)?.status ?? ''
        }`,
      );

      return response;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<any>;

      AxiosClientHTTP.traceFailedRequest('Login', url, config, axiosError);

      if (error instanceof Error) return error as R;
    }
  };

  post = async <T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: any): Promise<R> => {
    try {
      const response: R = await this.client.post<T, R>(url, data, config);

      AxiosClientHTTP.logger.debug(
        AxiosClientHTTP.TAG,
        `AxiosClientHTTP Post Function success - Url: ${AxiosClientHTTP.getUrlWithParams(url, config)} - Status: ${
          (response as unknown as AxiosResponse<T>)?.status ?? ''
        }`,
      );

      return response;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<any>;

      // Statut d'erreur declare comme attendu par l'appelant (reponse metier): on ne trace pas
      // d'erreur et on n'applique pas les effets de bord generiques, on relance simplement.
      if (AxiosClientHTTP.isExpectedStatus(axiosError, config)) {
        AxiosClientHTTP.logger.debug(
          AxiosClientHTTP.TAG,
          `AxiosClientHTTP Post Function - reponse metier attendue - Url: ${AxiosClientHTTP.getUrlWithParams(
            url,
            config,
          )} - Status: ${axiosError?.response?.status ?? ''}`,
        );

        throw axiosError;
      }

      AxiosClientHTTP.traceFailedRequest('Post', url, config, axiosError);

      AxiosClientHTTP.handleResponseError(axiosError);
      return error as R;
    }
  };

  get = async <T = any, R = AxiosResponse<T>>(url: string, config?: any): Promise<R> => {
    try {
      const response: R = await this.client.get<T, R>(url, config);

      AxiosClientHTTP.logger.debug(
        AxiosClientHTTP.TAG,
        `AxiosClientHTTP Get Function success - Url: ${AxiosClientHTTP.getUrlWithParams(url, config)} - Status: ${
          (response as unknown as AxiosResponse<T>)?.status ?? ''
        }`,
      );

      return response;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<any>;

      // Statut d'erreur declare comme attendu par l'appelant (reponse metier): on ne trace pas
      // d'erreur et on n'applique pas les effets de bord generiques, on relance simplement.
      if (AxiosClientHTTP.isExpectedStatus(axiosError, config)) {
        AxiosClientHTTP.logger.debug(
          AxiosClientHTTP.TAG,
          `AxiosClientHTTP Get Function - reponse metier attendue - Url: ${AxiosClientHTTP.getUrlWithParams(
            url,
            config,
          )} - Status: ${axiosError?.response?.status ?? ''}`,
        );

        throw axiosError;
      }

      AxiosClientHTTP.traceFailedRequest('Get', url, config, axiosError);

      AxiosClientHTTP.handleResponseError(axiosError);
      return error as R;
    }
  };

  put = async <T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: any): Promise<R> => {
    try {
      const response: R = await this.client.put<T, R>(url, data, config);

      AxiosClientHTTP.logger.debug(
        AxiosClientHTTP.TAG,
        `AxiosClientHTTP Put Function success - Url: ${AxiosClientHTTP.getUrlWithParams(url, config)} - Status: ${
          (response as unknown as AxiosResponse<T>)?.status ?? ''
        }`,
      );

      return response;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<any>;

      // Statut d'erreur declare comme attendu par l'appelant (reponse metier): on ne trace pas
      // d'erreur et on n'applique pas les effets de bord generiques, on relance simplement.
      if (AxiosClientHTTP.isExpectedStatus(axiosError, config)) {
        AxiosClientHTTP.logger.debug(
          AxiosClientHTTP.TAG,
          `AxiosClientHTTP Put Function - reponse metier attendue - Url: ${AxiosClientHTTP.getUrlWithParams(
            url,
            config,
          )} - Status: ${axiosError?.response?.status ?? ''}`,
        );

        throw axiosError;
      }

      // Echec pris en charge par l'appelant (compteur de tentatives, rejeu differe): on ne trace
      // pas d'erreur, on n'applique pas les effets de bord generiques et on relance simplement.
      if ((config as QcRequestConfig)?.silentOnError === true) {
        AxiosClientHTTP.logger.debug(
          AxiosClientHTTP.TAG,
          `AxiosClientHTTP Put Function - echec pris en charge par l'appelant - Url: ${AxiosClientHTTP.getUrlWithParams(
            url,
            config,
          )} - Status: ${axiosError?.response?.status ?? 'aucune reponse'} - Erreur: ${axiosError?.message ?? ''}`,
        );

        throw axiosError;
      }

      AxiosClientHTTP.traceFailedRequest('Put', url, config, axiosError);

      AxiosClientHTTP.handleResponseError(axiosError);
      return error as R;
    }
  };

  /**
   * Construit l'URL a tracer: un lien markdown dont le libelle est le path appele et la cible l'URL complete,
   * query params inclus. La trace reste courte tout en restant directement exploitable par le developpeur.
   *
   * @param url L'URL passee au client axios.
   * @param config La configuration axios de la requete (utilisee pour les query params).
   */
  private static getUrlWithParams = (url: string, config?: any): string => {
    const params = config?.params;
    let fullUrl = url;

    if (params !== undefined && params !== null && typeof params === 'object') {
      const queryString = Object.entries(params as Record<string, unknown>)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
        .join('&');

      if (queryString !== '') fullUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
    }

    try {
      const parsedUrl = new URL(fullUrl, config?.baseURL ?? window.location.origin);

      return `[${parsedUrl.pathname}${parsedUrl.search}](${parsedUrl.href})`;
    } catch {
      return fullUrl;
    }
  };

  /**
   * Trace l'echec d'une requete HTTP.
   *
   * Un 401 n'est pas une anomalie applicative mais la fin de la session: cookie QC expire ou
   * invalide, utilisateur non authentifie. Il est donc trace en avertissement et non en erreur.
   * Une seule expiration de session produit un 401 par requete en vol, et cette salve noyait les
   * vraies anomalies dans Application Insights. Les effets de bord (retour a l'ecran de
   * connexion, sortie du mode edition) restent appliques par handleResponseError.
   *
   * @param functionName Le nom de la methode du client, tel qu'il apparait dans la trace.
   * @param url L'URL appelee.
   * @param config La configuration axios de la requete.
   * @param error L'erreur axios remontee par la requete.
   */
  private static traceFailedRequest = (
    functionName: string,
    url: string,
    config: QcRequestConfig | undefined,
    error: AxiosError<any>,
  ): void => {
    const status = error?.response?.status;

    // Sans reponse HTTP exploitable, le code axios est la seule information qui permette de
    // distinguer une coupure reseau ou une reponse d'erreur privee d'en-tetes CORS (ERR_NETWORK),
    // un timeout (ECONNABORTED, ETIMEDOUT) et une annulation de la requete (ERR_CANCELED).
    const statusInfo = status ?? `aucune reponse (Code: ${error?.code ?? 'inconnu'})`;

    const message = `AxiosClientHTTP ${functionName} Function failed - Url: ${AxiosClientHTTP.getUrlWithParams(
      url,
      config,
    )} - Status: ${statusInfo} - Erreur: ${error?.message ?? ''}`;

    // Requete annulee volontairement par l'appelant (autocompletion remplacee par une frappe plus
    // recente, demontage du champ): c'est un comportement normal, pas une anomalie a tracer.
    if (error?.code === AxiosError.ERR_CANCELED) {
      AxiosClientHTTP.logger.debug(AxiosClientHTTP.TAG, `${message} - Requete annulee par l'appelant`);

      return;
    }

    if (status === HttpStatusCode.Unauthorized) {
      AxiosClientHTTP.logger.warn(AxiosClientHTTP.TAG, `${message} - Session expiree ou utilisateur non authentifie`);

      return;
    }

    // Aucune reponse HTTP recue: coupure reseau cote utilisateur, blocage par un proxy ou pare-feu, ou
    // reponse d'erreur privee d'en-tetes CORS. Ce n'est pas une anomalie applicative, et une seule
    // coupure produit une erreur par requete en vol: trace en avertissement, comme le 401.
    if (error?.code === AxiosError.ERR_NETWORK) {
      AxiosClientHTTP.logger.warn(AxiosClientHTTP.TAG, `${message} - Pas de reponse reseau (coupure, proxy ou CORS)`);

      return;
    }

    // Timeout: le serveur ou le reseau de l'utilisateur n'a pas repondu dans le delai imparti. Meme
    // raisonnement que la coupure reseau: pas une anomalie applicative, trace en avertissement.
    if (error?.code === AxiosError.ECONNABORTED || error?.code === AxiosError.ETIMEDOUT) {
      AxiosClientHTTP.logger.warn(AxiosClientHTTP.TAG, `${message} - Timeout`);

      return;
    }

    AxiosClientHTTP.logger.error(AxiosClientHTTP.TAG, message);
  };

  /**
   * Indique si le statut HTTP de l'erreur fait partie des statuts declares comme attendus
   * par l'appelant (config.expectedStatuses). Dans ce cas l'erreur n'est pas une anomalie
   * mais une reponse metier: elle ne doit pas polluer les traces d'erreur.
   *
   * @param error L'erreur axios remontee par la requete.
   * @param config La configuration axios de la requete.
   */
  private static isExpectedStatus = (error: AxiosError<any>, config?: QcRequestConfig): boolean => {
    const status = error?.response?.status;
    const expectedStatuses = config?.expectedStatuses;

    return status !== undefined && Array.isArray(expectedStatuses) && expectedStatuses.includes(status);
  };

  private static handleResponseError = (error: AxiosError<any>) => {
    const { response } = error;
    const {
      signInInfos: { userUPN },
    } = this.rootStore.LoginStore;
    let message: string;
    if (!response) {
      // pas de réponse -> api bloquée qlq part -> Afficher à l'utilisateur qu'il y a un problème avec l'API
      message = i18n.t('qcapp_warning_api_unavailable', { ns: 'axios' });
    } else if (userUPN) {
      const { status, data, config } = response;
      message = `Code: ${status ?? ''} - Message: ${data.message ?? ''} - Request: ${config.url ?? ''} - Method: ${
        config.method ?? ''
      } - Parameters: ${config.data} - Utilisateur: ${userUPN}`;
    } else {
      message = i18n.t('qcapp_warning_axios_generic_error', {
        ns: 'axios',
        details: `${error.message} - ${error.name}`,
      });
    }

    error.message = message;

    if (response && response.status === HttpStatusCode.Unauthorized) {
      AxiosClientHTTP.rootStore.LoginStore.setIsConnectedFor401HTTPCode(false);
    }

    throw error;
  };
}

export default AxiosClientHTTP;
